import { useCallback, useContext, useRef, useState } from "react";
import { Device, type Call } from "@twilio/voice-sdk";
import { TelephonyContext, type CallState, type CallOpts, type SmsArgs, type TelephonyApi } from "../telephony/context";
import { functionUrl, authHeaders } from "../telephony/util";

// useTelephony — the outbound click-to-call + send-SMS API used by LeadShow and
// the power dialer (useDialSession). It now PREFERS the shared Device from
// TelephonyProvider (desktop — one Device shared by inbound + outbound, so inbound
// calls also reach the agent). When no provider is present (mobile / any tree
// without it), it falls back to the original standalone lazy-Device below. The
// public surface is unchanged: { call, hangup, sendSms, callState, error, getCallSid }.

export type { CallOpts };

// Standalone lazy-Device implementation (the pre-3a.5 behavior). Outbound + SMS
// only; a Device is created on the first call(). No inbound registration.
function useStandaloneTelephony(): TelephonyApi {
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const callSidRef = useRef<string | null>(null);
  const [callState, setCallState] = useState<CallState>("idle");
  const [error, setError] = useState<string | null>(null);

  const ensureDevice = useCallback(async (): Promise<Device> => {
    if (deviceRef.current) return deviceRef.current;
    const res = await fetch(await functionUrl("voice-token"), { method: "POST", headers: await authHeaders() });
    if (!res.ok) throw new Error(`voice-token ${res.status}`);
    const { token } = await res.json();
    const device = new Device(token, { logLevel: "error" });
    await device.register();
    deviceRef.current = device;
    return device;
  }, []);

  const call = useCallback(async (to: string, leadId?: string, opts?: CallOpts) => {
    setError(null);
    setCallState("connecting");
    callSidRef.current = null;
    try {
      const device = await ensureDevice();
      const c = await device.connect({
        params: { To: to, lead_id: leadId ?? "", agent_user_id: opts?.agentUserId ?? "", session_id: opts?.sessionId ?? "" },
      });
      callRef.current = c;
      const grabSid = () => { callSidRef.current = c.parameters?.CallSid ?? callSidRef.current; };
      grabSid();
      c.on("ringing", () => { grabSid(); setCallState("ringing"); });
      c.on("accept", () => { grabSid(); setCallState("open"); });
      c.on("disconnect", () => setCallState("closed"));
      c.on("error", (e: Error) => { setError(e.message); setCallState("error"); });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setCallState("error");
    }
  }, [ensureDevice]);

  const hangup = useCallback(() => {
    callRef.current?.disconnect();
    callRef.current = null;
    setCallState("idle");
  }, []);

  const getCallSid = useCallback(() => callSidRef.current, []);

  const sendSms = useCallback(async (args: SmsArgs) => {
    const res = await fetch(await functionUrl("outbound-sms"), {
      method: "POST", headers: await authHeaders(), body: JSON.stringify(args),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.detail || data?.error || `send failed (${res.status})`);
    return data;
  }, []);

  return { call, hangup, sendSms, callState, error, getCallSid };
}

export function useTelephony(): TelephonyApi {
  const shared = useContext(TelephonyContext);
  const standalone = useStandaloneTelephony(); // lazy: no Device unless its call() runs
  return shared ?? standalone;
}
